/**
 * Loads Google Identity Services on demand, for One Tap.
 *
 * Previously the script sat in index.html as `<script async>` and LoginForm
 * touched the bare `google` global in a mount effect. That is a race: async
 * means the script may not have run yet, and `google?.accounts` does NOT
 * protect against this - optional chaining guards a null/undefined *value*,
 * but `google` is an undeclared *identifier* until the script defines it, so
 * evaluating it throws ReferenceError. Open the login modal on a slow
 * connection and the effect throws instead of degrading.
 *
 * Loading it here fixes both problems at once: the caller awaits a real signal
 * instead of hoping, and 98KB leaves the initial page load for a script only
 * the login modal needs.
 */

type GoogleIdentityServices = {
  accounts: {
    id: {
      initialize: (config: Record<string, unknown>) => void;
      prompt: () => void;
    };
  };
};

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

const SRC = "https://accounts.google.com/gsi/client";

let loader: Promise<GoogleIdentityServices> | undefined;

/**
 * Resolves with the GSI global once it is usable. Shared across callers, so
 * the script is requested at most once. A failed load clears the cached
 * promise so a later attempt - a second try at opening the modal - can retry
 * rather than being stuck with the first failure forever.
 */
export const loadGoogleIdentityServices =
  (): Promise<GoogleIdentityServices> => {
    if (loader) return loader;

    loader = new Promise<GoogleIdentityServices>((resolve, reject) => {
      // Accessed via window, never as a bare identifier, so a missing script is
      // undefined rather than a ReferenceError.
      if (window.google?.accounts?.id) {
        resolve(window.google);
        return;
      }

      const existing = document.querySelector<HTMLScriptElement>(
        `script[src="${SRC}"]`
      );
      const script = existing ?? document.createElement("script");

      script.addEventListener(
        "load",
        () => {
          if (window.google?.accounts?.id) resolve(window.google);
          else reject(new Error("GSI loaded but window.google.accounts is absent"));
        },
        { once: true }
      );
      script.addEventListener(
        "error",
        () => reject(new Error("Failed to load Google Identity Services")),
        { once: true }
      );

      if (!existing) {
        script.src = SRC;
        script.async = true;
        document.head.appendChild(script);
      }
    });

    loader.catch(() => {
      loader = undefined;
    });

    return loader;
  };
